import { Icon } from "@iconify/react";
import { LoginBox } from "../components/loginBox";
import { Header1, P } from "../components/headers";
import userBrowserLanguage from "../components/userBrowserLanguage";
import getTranslationJson from "../components/getTranslationJson";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { components as sso } from "../hooks/sso";
import axios, { AxiosError, type AxiosResponse } from "axios";
import { useForm } from "react-hook-form";
import { Link } from "../components/link";
import userTimeZoneCountryCode from "../components/userTimeZoneCountryCode";
import Input from "../components/input";
import { Button } from "../components/button";
import { useState } from "react";

interface FormValues {
  email: string;
  apiError?: string;
  country_code?: string;
}
function AuthPasswordReset() {
  const translation = getTranslationJson(userBrowserLanguage());
  const countryCode = userTimeZoneCountryCode();

  const queryClient = useQueryClient();

  const [successfullNotification, setSuccessfullNotification] = useState(false);

  // Mutations
  const mutationPasswordReset = useMutation({
    mutationKey: ["password_reset"],
    mutationFn: async (formData: FormValues): Promise<AxiosResponse<sso["schemas"]["ResetPasswordRequest"]>> => {
      setSuccessfullNotification(false);
      return axios.post(
        import.meta.env.VITE_PUBLIC_PLATFORM_SSO_API_URL! + "/reset-password/request",
        { email: formData.email },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["password_reset"] });
      setSuccessfullNotification(true);
    },
    onError: (data: AxiosError) => {
      // Invalidate and refetch
      console.error("Login error:", data);
      if (data.status === 404) {
        setError("apiError", {
          type: "custom",
          message:
            translation.error_account_not_found ||
            "Sorry, we couldn't find your account. Please check that your email address is spelled correctly.",
        });
      } else {
        setError("apiError", {
          type: "custom",
          message: data.message || "An error occurred while logging in.",
        });
      }
    },
  });

  const {
    register,
    handleSubmit,
    // control,
    formState: { errors },
    // getValues,
    // setValue,
    setError,
    clearErrors,
  } = useForm<FormValues>();

  const passwordHandleSubmit = (form_data: FormValues) => {
    if (import.meta.env.VITE_PUBLIC_DEV === "true") {
      console.log(form_data);
      console.log("Time Zone Country Code:", countryCode);
      console.log("Browser Language:", userBrowserLanguage());
    }
    clearErrors(["apiError", "email"]);
    mutationPasswordReset.mutate({ ...form_data, country_code: countryCode });
  };

  return (
    <div className="font-inter-24">
      <LoginBox>
        <header className="fflex flex-col mb-6 items-center justify-center">
          <Header1>{translation.forgot_your_password_title || "Forgot your password?"}</Header1>
          <P>
            {translation.forgot_your_password_description ||
              "Enter your email address below and we will send you a link to reset your password."}
          </P>
        </header>
        {errors.apiError && (
          <div className="mb-5 text-sm text-rose-600 bg-rose-50 py-3 px-4 rounded-md font-medium">
            {typeof errors.apiError.message === "string" ? errors.apiError.message : "An error occurred."}
          </div>
        )}
        {errors.email && (
          <div className="mb-5 text-sm text-rose-600 bg-rose-50 py-3 px-4 rounded-md font-medium">
            {translation.error_required_field || "Required field"}
          </div>
        )}
        <form onSubmit={handleSubmit(passwordHandleSubmit)}>
          <div className="flex flex-col gap-5">
            <input type="hidden" {...register("apiError")} />
            <Input
              {...register("email", { required: true })}
              type="text"
              label={translation.field_email_label || "Email"}
              placeholder={translation.field_email_placeholder || "ex: john_doe or john_doe@domain.com"}
              name="email"
            />
            <Button type="submit" disabled={mutationPasswordReset.isPending}>
              <Icon
                icon={mutationPasswordReset.isPending ? "prime:spinner" : "prime:send"}
                className={mutationPasswordReset.isPending ? "text-2xl mr-1 animate-spin" : "text-3xl mr-1 fill-white"}
              />
              {mutationPasswordReset.isPending
                ? `${translation.button_text_send_password_reset || "Send Reset Link"}...`
                : translation.button_text_send_password_reset || "Send Reset Link"}
            </Button>
          </div>
        </form>
        {successfullNotification && (
          <div className="my-5 text-sm text-brand-green bg-green-50 py-3 px-4 rounded-md font-medium text-center">
            {translation.notification_email_sent_successfully || "Email sent successfully."}
          </div>
        )}
        <div>
          <footer className="text-center text-sm text-slate-600 mt-7">
            {translation.remember_your_password || "Remember your password?"}&nbsp;
            <Link href="/auth/login">{translation.sign_in_here || "Sign in here"}</Link>
          </footer>
        </div>
      </LoginBox>
    </div>
  );
}
export default AuthPasswordReset;
